import * as THREE from 'three';

/** `conflict` is Unity's red obstruction outline; `select` the ordinary blue one. */
export type SelectionTone = 'select' | 'conflict';
export interface SelectionPresentationOptions { enabled: boolean; model: boolean; tone?: SelectionTone }
interface Shell { mesh: THREE.Mesh; key: string }

/** Lite PlanSceneBuilder.attachHighlight / ObjModelLoader.shell. This root belongs to
 * the scene, never the model: 2D clones and gesture hit lists must stay unadorned. */
export class SelectionPresentation {
  readonly root = new THREE.Group();
  private target: THREE.Object3D | null = null;
  private model = true;
  private tone: SelectionTone = 'select';
  private shells = new Map<string, Shell>();
  private materials: THREE.MeshStandardMaterial[] = [];
  private disposed = false;
  private inverse = new THREE.Matrix4();

  constructor(private readonly color?: string) {
    this.root.name = 'selection-presentation';
    this.root.userData.selectionPresentation = true;
    this.root.matrixAutoUpdate = false;
    this.root.visible = false;
  }

  update(target: THREE.Object3D | null, options: SelectionPresentationOptions): void {
    if (this.disposed) return;
    const tone = options.tone ?? 'select';
    if (target !== this.target || options.model !== this.model || tone !== this.tone) {
      this.clear(); this.target = target; this.model = options.model; this.tone = tone;
    }
    this.root.visible = !!target && options.enabled && visibleAncestors(target);
    if (!target || !this.root.visible) return;
    target.updateWorldMatrix(true, true);
    this.inverse.copy(target.matrixWorld).invert();
    this.root.matrix.copy(target.matrixWorld);
    if (this.root.parent) {
      this.root.parent.updateWorldMatrix(true, false);
      this.root.matrix.premultiply(new THREE.Matrix4().copy(this.root.parent.matrixWorld).invert());
    }
    if (!this.materials.length) {
      this.materials = [outlineMaterial(1, this.tone, this.color)];
      if (!this.model) this.materials.push(outlineMaterial(.22, this.tone, this.color));
    }
    if (this.model) this.updateModel(target);
    else this.updatePrimitive(target);
    this.root.visible = this.shells.size > 0;
    this.root.updateMatrixWorld(true);
  }

  private updateModel(target: THREE.Object3D): void {
    const retained = new Set<string>();
    target.traverseVisible(node => {
      if (!(node instanceof THREE.Mesh) || node.userData.selectionPresentation) return;
      const geometry = node.geometry;
      if (!geometry.getAttribute('position')?.count) return;
      const local = this.inverse.clone().multiply(node.matrixWorld);
      // Native divides the metre offset by the smallest fitting axis, then applies
      // the original nonuniform/mirrored fit to the shell. Excluding the target pose
      // also lets its landing squash animate the shell without a per-frame rebuild.
      const scale = new THREE.Vector3().setFromMatrixScale(local);
      const fit = Math.max(Math.min(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z)), 1e-6);
      const offset = .0126 / fit;
      const key = geometryStamp(geometry) + ':' + offset.toPrecision(9);
      let shell = this.shells.get(node.uuid);
      if (shell?.key !== key) {
        this.removeShell(node.uuid);
        shell = this.addShell(node.uuid, expandedTriangles(geometry, offset), this.materials[0], key);
      }
      shell.mesh.matrix.copy(local); retained.add(node.uuid);
    });
    for (const key of this.shells.keys()) if (!retained.has(key)) this.removeShell(key);
  }

  private updatePrimitive(target: THREE.Object3D): void {
    const bounds = new THREE.Box3(), point = new THREE.Vector3();
    target.traverseVisible(node => {
      if (!(node instanceof THREE.Mesh) || node.userData.selectionPresentation) return;
      const positions = node.geometry.getAttribute('position');
      if (!positions) return;
      const local = this.inverse.clone().multiply(node.matrixWorld);
      for (let i = 0; i < positions.count; i++) bounds.expandByPoint(point.fromBufferAttribute(positions, i).applyMatrix4(local));
    });
    if (bounds.isEmpty()) { for (const key of this.shells.keys()) this.removeShell(key); return; }
    const center = bounds.getCenter(new THREE.Vector3()), size = bounds.getSize(new THREE.Vector3());
    const key = [...center.toArray(), ...size.toArray()].map(n => n.toPrecision(9)).join(',');
    for (const [i, offset] of [.018, .06].entries()) {
      const id = `primitive:${i}`;
      let shell = this.shells.get(id);
      if (shell?.key !== key) {
        this.removeShell(id);
        shell = this.addShell(id, new THREE.BoxGeometry(size.x + offset * 2, size.y + offset * 2, size.z + offset * 2), this.materials[i], key);
      }
      shell.mesh.matrix.makeTranslation(center.x, center.y, center.z);
    }
  }

  private addShell(id: string, geometry: THREE.BufferGeometry, material: THREE.Material, key: string): Shell {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'selection-shell'; mesh.userData.selectionPresentation = true;
    mesh.matrixAutoUpdate = false; mesh.castShadow = false; mesh.receiveShadow = false;
    mesh.raycast = () => {}; // Defensive even if a consumer raycasts the whole scene.
    this.root.add(mesh);
    const shell = { mesh, key }; this.shells.set(id, shell); return shell;
  }

  private removeShell(id: string): void {
    const shell = this.shells.get(id);
    if (!shell) return;
    shell.mesh.removeFromParent(); shell.mesh.geometry.dispose(); this.shells.delete(id);
  }

  clear(): void {
    for (const id of this.shells.keys()) this.removeShell(id);
    for (const material of this.materials) material.dispose();
    this.materials = []; this.target = null; this.tone = 'select'; this.root.visible = false;
  }

  dispose(): void { if (this.disposed) return; this.clear(); this.root.removeFromParent(); this.disposed = true; }
}

function visibleAncestors(target: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = target; node; node = node.parent) if (!node.visible) return false;
  return true;
}

/** The self-lit accent (or red) back-face shell material; the wall and floor outlines share it. */
export function outlineMaterial(opacity: number, tone: SelectionTone, participantColor?: string): THREE.MeshStandardMaterial {
  // The obstruction outline is the native accent's red counterpart, lit the same way, so a
  // collision reads as the selection changing state rather than as a second kind of shell.
  const color = participantColor ? new THREE.Color(participantColor) : tone === 'conflict' ? new THREE.Color().setRGB(1, .23, .19, THREE.SRGBColorSpace)
    : new THREE.Color().setRGB(.25, .52, 1, THREE.SRGBColorSpace);
  const material = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 2.5,
    roughness: 1, metalness: 0, side: THREE.BackSide, opacity, transparent: opacity < 1, toneMapped: false });
  // RealityKit and Three do not display identical PBR emission identically. Measured
  // native shell pixels are (93,186,255); Three ACES produced (173,209,244), and merely
  // disabling ACES still admitted cyan scene lighting. A single linear gain of 2.1
  // on the native accent yields (92,186,255). Treat this editing affordance as self-lit
  // display color, retaining native shell depth/alpha and the source material values.
  // Presence colours are already display colours from the avatar: apply no native-blue gain.
  material.onBeforeCompile = shader => {
    shader.uniforms.selectionDisplayColor = { value: color.clone().multiplyScalar(participantColor ? 1 : 2.1) };
    shader.fragmentShader = 'uniform vec3 selectionDisplayColor;\n' + shader.fragmentShader.replace(
      '#include <opaque_fragment>', 'outgoingLight = selectionDisplayColor;\n#include <opaque_fragment>');
  };
  material.customProgramCacheKey = () => 'native-selection-display-v1';
  return material;
}

function geometryStamp(geometry: THREE.BufferGeometry): string {
  const version = (attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined | null) => {
    if (!attribute) return '-';
    return `${attribute.count}:${attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.version : attribute.version}`;
  };
  return [geometry.uuid, version(geometry.getAttribute('position')), version(geometry.getAttribute('normal')),
    version(geometry.index), geometry.drawRange.start, geometry.drawRange.count].join(':');
}

/** Copy triangles, reconcile winding with their authored normals, then extrude.
 * No source attributes, bounding boxes, materials or GPU ownership are changed. */
function expandedTriangles(source: THREE.BufferGeometry, offset: number): THREE.BufferGeometry {
  const working = source.getAttribute('normal') ? source : source.clone();
  if (working !== source) working.computeVertexNormals();
  const input = working.getAttribute('position'), normals = working.getAttribute('normal'), index = working.index;
  const end = Math.min(index?.count ?? input.count, source.drawRange.start + source.drawRange.count);
  const out: number[] = [], outNormals: number[] = [];
  const p = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  const n = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  const ab = new THREE.Vector3(), ac = new THREE.Vector3(), average = new THREE.Vector3();
  for (let i = source.drawRange.start; i + 2 < end; i += 3) {
    for (let j = 0; j < 3; j++) {
      const at = index ? index.getX(i + j) : i + j;
      p[j].fromBufferAttribute(input, at); n[j].fromBufferAttribute(normals, at);
    }
    average.copy(n[0]).add(n[1]).add(n[2]).addScalar(1e-6);
    const flip = ab.subVectors(p[1], p[0]).cross(ac.subVectors(p[2], p[0])).dot(average) < 0;
    for (const j of flip ? [0, 2, 1] : [0, 1, 2]) {
      out.push(p[j].x + n[j].x * offset, p[j].y + n[j].y * offset, p[j].z + n[j].z * offset);
      outNormals.push(n[j].x, n[j].y, n[j].z);
    }
  }
  if (working !== source) working.dispose();
  const result = new THREE.BufferGeometry();
  result.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
  result.setAttribute('normal', new THREE.Float32BufferAttribute(outNormals, 3));
  result.computeBoundingBox(); result.computeBoundingSphere();
  return result;
}
